import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import nocobaseService from '../services/nocobaseService'
import { formatPlainTextContent, formatPlainTextExcerpt } from '../utils/textFormatting.jsx'
import { sanitizeRichText } from '../utils/sanitize'
import Header from './Header'
import Footer from './Footer'

const DynamicInsightPage = ({ onOpenChat }) => {
  const { slug, id, lang } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { currentLanguage, changeLanguage } = useLanguage()
  
  // Determine the language to use - prioritize URL lang over context
  const activeLanguage = (lang && (lang === 'ar' || lang === 'en')) ? lang : currentLanguage
  
  // Update language from URL if provided, or redirect to language-specific URL
  useEffect(() => {
    if (lang && (lang === 'ar' || lang === 'en')) {
      // Language is in URL, update context if different
      if (lang !== currentLanguage) {
        changeLanguage(lang)
      }
    } else if ((slug || id) && currentLanguage) {
      // No language in URL, redirect to include language (only once)
      const redirectUrl = id 
        ? `/${currentLanguage}/insights/id/${id}`
        : `/${currentLanguage}/insights/${slug}`
      navigate(redirectUrl, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, slug, id])
  
  // Update URL when language changes in context
  useEffect(() => {
    if ((slug || id) && currentLanguage && lang && lang !== currentLanguage && (lang === 'ar' || lang === 'en')) {
      const redirectUrl = id 
        ? `/${currentLanguage}/insights/id/${id}`
        : `/${currentLanguage}/insights/${slug}`
      navigate(redirectUrl, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage])
  
  // Use activeLanguage for display
  const displayLanguage = activeLanguage
  
  const [insight, setInsight] = useState(null)
  const [relatedInsights, setRelatedInsights] = useState([])
  const [relatedServices, setRelatedServices] = useState([])
  const [author, setAuthor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchInsight = async () => {
      try {
        setLoading(true)
        
        // Get all posts from NocoBase
        const allPosts = await nocobaseService.getPosts({ status: 'Published' })
        console.log('📊 Available posts:', allPosts.map(i => ({
          name: i.Name,
          id: i.id,
          slug: i.slug,
          Post_Type: i.Post_Type
        })))
        
        let currentInsight;
        
        if (id) {
          // ID-based navigation (from homepage cards or blog page)
          console.log('🔍 Looking for post with ID:', id)
          // Convert id to number for comparison
          const numericId = parseInt(id, 10)
          currentInsight = allPosts.find(i => {
            const postId = parseInt(i.id, 10)
            return postId === numericId || String(postId) === String(id) || String(i.id) === String(id)
          })
          console.log('🎯 Found post by ID:', currentInsight?.Name || 'Not found', {
            searchedId: id,
            foundId: currentInsight?.id,
            foundName: currentInsight?.Name
          })
        } else if (slug) {
          // Slug-based navigation (from URLs or other links)
          const decodedSlug = decodeURIComponent(slug)
          console.log('🔍 Looking for post with slug:', decodedSlug)
          
          currentInsight = allPosts.find(i => {
            const postSlug = i.slug
            console.log('🔍 Comparing slugs for:', i.Name, {
              recordId: i.id,
              postSlug: postSlug,
              targetSlug: decodedSlug,
              exactMatch: postSlug === decodedSlug
            })
            return postSlug === decodedSlug || postSlug === slug
          })
          console.log('🎯 Found post by slug:', currentInsight?.Name || 'Not found')
        }
        
        console.log('🎯 Found post:', currentInsight?.Name || 'Not found')
        
        if (!currentInsight) {
          const searchTerm = id || slug
          console.warn(`❌ No post found for: ${searchTerm}`)
          console.log('🔧 Available posts:', allPosts.map(i => ({
            name: i.Name,
            id: i.id,
            slug: i.slug
          })))
          
          // Fallback: try to find by partial name match
          if (slug) {
            const decodedSlug = decodeURIComponent(slug)
            const fallbackInsight = allPosts.find(i => {
              const nameParts = i.Name?.toLowerCase().split(' ') || []
              const slugParts = decodedSlug.toLowerCase().split('-')
              return nameParts.some(part => slugParts.includes(part))
            })
            
            if (fallbackInsight) {
              console.log('🔄 Using fallback post:', fallbackInsight.Name)
              setInsight(fallbackInsight)
              const otherPosts = allPosts.filter(i => i.id !== fallbackInsight.id)
              setRelatedInsights(otherPosts)
              return
            }
          }
          
          setError(`Post not found for: ${searchTerm}`)
          return
        }
        
        setInsight(currentInsight)
        
        // Debug: Log all available fields
        console.log('📊 Post data (formatted):', {
          id: currentInsight.id,
          title: currentInsight.Name,
          tags: currentInsight.tags,
          category: currentInsight.Category,
          authorId: currentInsight.authorId,
          authorIds: currentInsight.authorIds,
          teamMembers: currentInsight.teamMembers,
          linkedServiceIds: currentInsight.linkedServiceIds,
          services: currentInsight.services,
          Post_Type: currentInsight.Post_Type
        });
        console.log('📊 Post raw data (all fields):', currentInsight);
        
        // Get other posts for navigation
        const otherPosts = allPosts.filter(i => i.id !== currentInsight.id)
        setRelatedInsights(otherPosts)
        
        // Fetch author details if post type is Articles
        // Check for authorId or try to find author from teamMembers
        const authorIdToUse = currentInsight.authorId || 
                              (Array.isArray(currentInsight.authorIds) && currentInsight.authorIds.length > 0 ? currentInsight.authorIds[0] : null) ||
                              (Array.isArray(currentInsight.teamMembers) && currentInsight.teamMembers.length > 0 ? currentInsight.teamMembers[0] : null);
        
        // Check if post type is Articles (with or without space)
        const isArticle = currentInsight.Post_Type === 'Articles ' || currentInsight.Post_Type === 'Articles';
        
        if (isArticle && authorIdToUse) {
          try {
            const allTeamMembers = await nocobaseService.getTeamMembers(true, displayLanguage)
            // Find team member by ID
            const authorData = allTeamMembers.find(member => 
              member.id === authorIdToUse || 
              String(member.id) === String(authorIdToUse) ||
              member.name === authorIdToUse ||
              member.name_en === authorIdToUse ||
              member.name_ar === authorIdToUse
            )
            if (authorData) {
              setAuthor(authorData)
              console.log('👤 Author found:', authorData.name)
            } else {
              console.log('⚠️ Author not found for ID:', authorIdToUse)
              // Fallback: try to find by name if ID not found
              if (currentInsight.teamMembers && currentInsight.teamMembers.length > 0) {
                const fallbackAuthor = allTeamMembers.find(member => 
                  currentInsight.teamMembers.some(authorName => 
                    member.name === authorName ||
                    member.name_en === authorName ||
                    member.name_ar === authorName
                  )
                )
                if (fallbackAuthor) {
                  setAuthor(fallbackAuthor)
                  console.log('👤 Author found by name (fallback):', fallbackAuthor.name)
                } else {
                  setAuthor(null)
                }
              } else {
                setAuthor(null)
              }
            }
          } catch (authorError) {
            console.error('Failed to fetch author:', authorError)
            setAuthor(null)
          }
        } else {
          setAuthor(null)
        }
        
        // Fetch related services using Lined_services field (Links field with IDs)
        // Wrap in try-catch to prevent blocking page load if services fetch fails
        try {
          const linkedServiceIds = currentInsight.linkedServiceIds || []
          console.log('🔍 Checking for linked services:', {
            hasLinkedServiceIds: Array.isArray(linkedServiceIds) && linkedServiceIds.length > 0,
            linkedServiceIdsCount: linkedServiceIds.length,
            linkedServiceIds: linkedServiceIds
          })
          
          if (Array.isArray(linkedServiceIds) && linkedServiceIds.length > 0) {
            try {
              // Get all subservices (Lined_services links to Subservices table)
              const allSubservices = await nocobaseService.getSubservices(null, true, displayLanguage)
              
              if (Array.isArray(allSubservices)) {
                console.log('📋 All available subservices:', allSubservices.length)
                
                // Filter subservices by linked IDs
                const relatedServicesData = allSubservices.filter(subservice => {
                  try {
                    const subserviceId = subservice?.id || subservice?.Id
                    if (!subserviceId) return false
                    
                    const isLinked = linkedServiceIds.some(linkedId => {
                      if (linkedId == null || linkedId === undefined) return false
                      return String(subserviceId) === String(linkedId) || 
                             Number(subserviceId) === Number(linkedId)
                    })
                    
                    return isLinked
                  } catch (filterError) {
                    console.warn('Error filtering subservice:', filterError)
                    return false
                  }
                })
                
                console.log('🔗 Related services found:', relatedServicesData.length)
                setRelatedServices(relatedServicesData)
              } else {
                console.warn('⚠️ getSubservices returned non-array:', typeof allSubservices)
                setRelatedServices([])
              }
            } catch (serviceError) {
              console.error('❌ Failed to fetch related services:', serviceError)
              setRelatedServices([])
            }
          } else {
            console.log('⚠️ No linked service IDs found in post data')
            setRelatedServices([])
          }
        } catch (servicesError) {
          console.error('❌ Error in services fetch logic:', servicesError)
          setRelatedServices([])
        }
        
        // Scroll to top when post loads
        window.scrollTo({ top: 0, behavior: 'smooth' })
        
      } catch (err) {
        console.error('Failed to fetch post:', err)
        setError('Failed to load post')
      } finally {
        setLoading(false)
      }
    }

    if (slug || id) {
      fetchInsight()
    }
  }, [slug, id, displayLanguage])

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString(displayLanguage === 'ar' ? 'ar-SA' : 'en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })
  }

  const getTitle = (insight) => {
    return insight[`title_${displayLanguage}`] || insight.title_en || insight.Name || ''
  }

  const getContent = (insight) => {
    return insight[`content_${displayLanguage}`] || insight.content_en || insight.content || ''
  }

  const getExcerpt = (insight) => {
    return insight[`excerpt_${displayLanguage}`] || insight.excerpt_en || insight.Summary || ''
  }

  const handleRelatedInsightClick = (relatedInsight, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const relatedId = relatedInsight.id;
    const url = `/${displayLanguage}/insights/id/${relatedId}`
    
    console.log('🔗 Related insight click:', {
      name: relatedInsight.Name,
      id: relatedId,
      targetUrl: url,
      currentPage: slug || id
    })
    
    // Navigate to the related insight using ID
    navigate(url)
    
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }, 100)
  }

  if (loading) {
    return (
      <div className="insight-page" style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column'
      }}>
        <Header onOpenChat={onOpenChat} />
        <section style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
        }}>
          <div style={{ fontSize: '18px', color: '#fff' }}>
            {t('common.loading')}
          </div>
        </section>
        <div style={{ marginTop: 'auto', paddingTop: '60px' }}>
          <Footer />
        </div>
      </div>
    )
  }

  if (error || !insight) {
    return (
      <div className="insight-page" style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column'
      }}>
        <Header onOpenChat={onOpenChat} />
        <section style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
        }}>
          <div style={{ fontSize: '18px', color: '#fff', textAlign: 'center' }}>
            <p>{t('common.error')} - {error}</p>
            <Link 
              to="/" 
              style={{
                color: '#fff',
                textDecoration: 'underline',
                fontSize: '16px'
              }}
            >
              Return to Homepage
            </Link>
          </div>
        </section>
        <div style={{ marginTop: 'auto', paddingTop: '60px' }}>
          <Footer />
        </div>
      </div>
    )
  }

  return (
    <div className="insight-page" style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column'
    }}>
      <Header onOpenChat={onOpenChat} />
      
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        padding: '120px 0 80px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px',
          position: 'relative',
          zIndex: 2
        }}>
          {/* Breadcrumb */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <Link 
              to="/" 
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                marginRight: '10px'
              }}
            >
              {displayLanguage === 'ar' ? 'الرئيسية' : 'Home'}
            </Link>
            <span style={{ color: 'rgba(255, 255, 255, 0.5)', margin: '0 10px' }}>/</span>
            <Link 
              to="/#insights" 
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                marginRight: '10px'
              }}
            >
              {displayLanguage === 'ar' ? 'مدونة التحديثات القانونية' : 'Legal Updates Blog'}
            </Link>
            <span style={{ color: 'rgba(255, 255, 255, 0.5)', margin: '0 10px' }}>/</span>
            <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
              {getTitle(insight)}
            </span>
          </div>

          {/* Article Meta */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            marginBottom: '30px',
            flexWrap: 'wrap'
          }}>
            <span style={{
              padding: '6px 12px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {(() => {
                const postType = insight.Post_Type || '';
                const category = insight.Category || insight.category;
                console.log('📂 Category check:', { postType, category, hasCategory: !!category });
                
                if (postType === 'Articles ' || postType === 'Articles') {
                  return displayLanguage === 'ar' ? 'مقالات' : 'Articles';
                } else if (postType === 'Events') {
                  return displayLanguage === 'ar' ? 'فعاليات' : 'Events';
                } else if (postType === 'Social Media ' || postType === 'Social Media') {
                  return displayLanguage === 'ar' ? 'منشورات' : 'Social Media';
                } else if (category) {
                  return category;
                } else {
                  return 'Legal Update';
                }
              })()}
            </span>
            <span style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.8)'
            }}>
              {formatDate(insight.published_at || insight.Date || insight.date)}
            </span>
            {insight.Source && (
              <span style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.8)'
              }}>
                {displayLanguage === 'ar' ? 'بقلم' : 'By'} {insight.Source}
              </span>
            )}
          </div>

          {/* Title and Image Section */}
          <div 
            className="title-image-section"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '30px',
              marginBottom: '20px',
              flexDirection: displayLanguage === 'ar' ? 'row-reverse' : 'row',
              flexWrap: 'wrap'
            }}>
            {/* Title */}
            <div style={{ flex: '1', minWidth: '300px' }}>
              <h1 style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(32px, 6vw, 56px)',
                fontWeight: 700,
                lineHeight: 1.2,
                margin: '0',
                letterSpacing: '-0.02em',
                textAlign: displayLanguage === 'ar' ? 'right' : 'left'
              }}>
                {getTitle(insight)}
              </h1>
            </div>
            
            {/* Image Box */}
            <div style={{
              width: '400px',
              height: '300px',
              borderRadius: '15px',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              flexShrink: 0,
              alignSelf: 'flex-start'
            }}>
              <img 
                src={insight.image || insight.background_image || '/pic/1.jpg'} 
                alt={getTitle(insight)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
                onError={(e) => {
                  e.target.src = '/pic/1.jpg';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article Content with Side Services */}
      <section style={{
        padding: '80px 0',
        background: '#fff'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px',
          display: 'grid',
          gridTemplateColumns: relatedServices && relatedServices.length > 0 ? 
            (displayLanguage === 'ar' ? '300px 1fr' : '1fr 300px') : '1fr',
          gap: '40px',
          alignItems: 'start'
        }}>
          {/* Main Content */}
          <div 
            style={{
              fontFamily: 'Inter, sans-serif',
              textAlign: displayLanguage === 'ar' ? 'right' : 'left',
              order: displayLanguage === 'ar' ? 2 : 1
            }}
          >
            {/* Rich Text Content */}
            {(() => {
              const content = getContent(insight);
              if (!content) return null;
              
              // Check if content contains HTML
              const isHtml = typeof content === 'string' && /<[a-z][\s\S]*>/i.test(content);
              
              if (isHtml) {
                // Render as rich text (HTML)
                return (
                  <div 
                    style={{
                      fontSize: '16px',
                      lineHeight: '1.7',
                      color: '#374151',
                      fontFamily: 'Inter, sans-serif'
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeRichText(content) }}
                  />
                );
              } else {
                // Render as plain text with formatting
                return formatPlainTextContent(content);
              }
            })()}
            
            {/* Author Section - Only for Articles */}
            {author && (insight.Post_Type === 'Articles ' || insight.Post_Type === 'Articles') && (
              <div style={{
                marginTop: '60px',
                padding: '30px',
                background: 'rgba(26, 26, 26, 0.03)',
                borderRadius: '15px',
                border: '1px solid rgba(26, 26, 26, 0.1)'
              }}>
                <h3 style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#1a1a1a',
                  margin: '0 0 20px 0',
                  textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                }}>
                  {displayLanguage === 'ar' ? 'عن الكاتب' : 'About the Author'}
                </h3>
                
                <div style={{
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'flex-start',
                  flexDirection: displayLanguage === 'ar' ? 'row-reverse' : 'row'
                }}>
                  {/* Author Image */}
                  {author.image && (
                    <div style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      flexShrink: 0,
                      border: '3px solid rgba(26, 26, 26, 0.1)'
                    }}>
                      <img 
                        src={author.image} 
                        alt={author.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.src = '/pic/1.jpg';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Author Info */}
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#1a1a1a',
                      margin: '0 0 8px 0',
                      textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                    }}>
                      {author.name}
                    </h4>
                    
                    {author.position && (
                      <p style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        color: '#666',
                        margin: '0 0 12px 0',
                        textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                      }}>
                        {author.position}
                      </p>
                    )}
                    
                    {author.specialization && (
                      <p style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        color: '#333',
                        lineHeight: 1.6,
                        margin: '0',
                        textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                      }}>
                        {typeof author.specialization === 'string' 
                          ? author.specialization 
                          : (Array.isArray(author.specialization) ? author.specialization.join(', ') : '')}
                      </p>
                    )}
                    
                    {(author.bio || author.about) && (
                      <div 
                        style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '14px',
                          color: '#555',
                          lineHeight: 1.6,
                          margin: '12px 0 0 0',
                          textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: sanitizeRichText(author.bio || (typeof author.about === 'string' && author.about.includes('<') 
                            ? author.about 
                            : `<p>${author.about || ''}</p>`)) 
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Tags Section - Show if tags exist */}
            {(() => {
              const tags = insight.tags || [];
              const hasTags = Array.isArray(tags) && tags.length > 0;
              if (hasTags) {
                console.log('🏷️ Tags found:', tags);
              } else {
                console.log('🏷️ No tags found:', { tags, tagsType: typeof tags });
              }
              return hasTags;
            })() && (
              <div style={{
                marginTop: '40px',
                padding: '20px',
                background: 'rgba(26, 26, 26, 0.02)',
                borderRadius: '12px',
                border: '1px solid rgba(26, 26, 26, 0.05)'
              }}>
                <h4 style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#666',
                  margin: '0 0 12px 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                }}>
                  {displayLanguage === 'ar' ? 'الوسوم' : 'Tags'}
                </h4>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                  justifyContent: displayLanguage === 'ar' ? 'flex-end' : 'flex-start'
                }}>
                  {insight.tags.map((tag, index) => (
                    <span key={index} style={{
                      padding: '6px 12px',
                      background: 'rgba(26, 26, 26, 0.08)',
                      border: '1px solid rgba(26, 26, 26, 0.15)',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#333',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      {typeof tag === 'string' ? tag : (tag.name || tag.title || String(tag))}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Related Services Section - Show for all post types */}
            {(() => {
              const hasServices = relatedServices && Array.isArray(relatedServices) && relatedServices.length > 0;
              if (hasServices) {
                console.log('🔗 Related Services found:', relatedServices.length);
              } else {
                console.log('🔗 No Related Services:', { relatedServices, type: typeof relatedServices, isArray: Array.isArray(relatedServices) });
              }
              return hasServices;
            })() && (
              <div style={{
                marginTop: '60px',
                padding: '40px',
                background: 'rgba(26, 26, 26, 0.03)',
                borderRadius: '20px',
                border: '1px solid rgba(26, 26, 26, 0.1)'
              }}>
                <h3 style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  margin: '0 0 30px 0',
                  textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                }}>
                  {displayLanguage === 'ar' ? 'الخدمات المرتبطة' : 'Related Services'}
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '20px'
                }}>
                  {relatedServices.map((service, index) => {
                    const serviceId = service.id || service.Id
                    return (
                    <button
                      key={serviceId}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        console.log('🔗 Main section - Navigating to service:', {
                          serviceId: serviceId,
                          title: service.title || service.title_en || service.title_ar,
                          url: `/${currentLanguage}/subservice/${serviceId}`
                        })
                        navigate(`/${displayLanguage}/subservice/${serviceId}`)
                        // Scroll to top after navigation
                        setTimeout(() => {
                          window.scrollTo({ top: 0, behavior: 'smooth' })
                        }, 100)
                      }}
                      style={{
                        background: '#fff',
                        border: '2px solid rgba(26, 26, 26, 0.1)',
                        borderRadius: '12px',
                        padding: '20px',
                        textAlign: displayLanguage === 'ar' ? 'right' : 'left',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        alignItems: displayLanguage === 'ar' ? 'flex-end' : 'flex-start'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(26, 26, 26, 0.05)'
                        e.currentTarget.style.borderColor = '#1a1a1a'
                        e.currentTarget.style.transform = 'translateY(-4px)'
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(26, 26, 26, 0.15)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#fff'
                        e.currentTarget.style.borderColor = 'rgba(26, 26, 26, 0.1)'
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                    >
                      {/* Service Icon */}
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #1a1a1a, #333)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '20px',
                        flexShrink: 0
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                          <path d="M2 17l10 5 10-5"/>
                          <path d="M2 12l10 5 10-5"/>
                        </svg>
                      </div>
                      
                      {/* Service Info */}
                      <div style={{ flex: 1, width: '100%' }}>
                        <div style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '18px',
                          fontWeight: 600,
                          color: '#1a1a1a',
                          marginBottom: '8px',
                          lineHeight: '1.3'
                        }}>
                          {displayLanguage === 'ar' ? (service.title_ar || service.title) : (service.title_en || service.title || service.name)}
                        </div>
                        {service.description && (
                          <div style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '14px',
                            color: '#666',
                            lineHeight: '1.5',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {displayLanguage === 'ar' ? (service.description_ar || service.description) : (service.description_en || service.description)}
                          </div>
                        )}
                        <div style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '12px',
                          color: '#1a1a1a',
                          marginTop: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {displayLanguage === 'ar' ? 'عرض التفاصيل →' : 'View Details →'}
                        </div>
                      </div>
                    </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Related Services Sidebar */}
          {relatedServices && relatedServices.length > 0 && (
            <div style={{
              order: displayLanguage === 'ar' ? 1 : 2,
              position: 'sticky',
              top: '100px'
            }}>
              <div style={{
                background: 'rgba(26, 26, 26, 0.05)',
                borderRadius: '15px',
                padding: '25px',
                border: '1px solid rgba(26, 26, 26, 0.1)'
              }}>
                <h3 style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#1a1a1a',
                  margin: '0 0 20px 0',
                  textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                }}>
                  {displayLanguage === 'ar' ? 'الخدمات المرتبطة' : 'Related Services'}
                </h3>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  {relatedServices.map((service, index) => {
                    const serviceId = service.id || service.Id
                    return (
                    <button
                      key={serviceId}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        console.log('🔗 Sidebar - Navigating to service:', {
                          serviceId: serviceId,
                          title: service.title || service.title_en || service.title_ar,
                          url: `/${currentLanguage}/subservice/${serviceId}`
                        })
                        navigate(`/${displayLanguage}/subservice/${serviceId}`)
                        // Scroll to top after navigation
                        setTimeout(() => {
                          window.scrollTo({ top: 0, behavior: 'smooth' })
                        }, 100)
                      }}
                      style={{
                        background: 'rgba(26, 26, 26, 0.02)',
                        border: '1px solid rgba(26, 26, 26, 0.1)',
                        borderRadius: '10px',
                        padding: '15px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(26, 26, 26, 0.08)'
                        e.target.style.borderColor = '#1a1a1a'
                        e.target.style.transform = 'translateY(-2px)'
                        e.target.style.boxShadow = '0 4px 15px rgba(26, 26, 26, 0.1)'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(26, 26, 26, 0.02)'
                        e.target.style.borderColor = 'rgba(26, 26, 26, 0.1)'
                        e.target.style.transform = 'translateY(0)'
                        e.target.style.boxShadow = 'none'
                      }}
                    >
                      {/* Service Icon */}
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #1a1a1a, #333)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '16px',
                        flexShrink: 0
                      }}>
                        ⚖️
                      </div>
                      
                      {/* Service Info */}
                      <div style={{ flex: 1, textAlign: displayLanguage === 'ar' ? 'right' : 'left' }}>
                        <div style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#1a1a1a',
                          marginBottom: '4px',
                          lineHeight: '1.3'
                        }}>
                          {service.title || service.name}
                        </div>
                        <div style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '11px',
                          color: '#666',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {displayLanguage === 'ar' ? 'عرض التفاصيل →' : 'View Details →'}
                        </div>
                      </div>
                    </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>


      {/* All Other Insights Navigation */}
      <section style={{
        padding: '60px 0',
        background: '#f8f9fa'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <h2 style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 'clamp(28px, 5vw, 36px)',
            fontWeight: 700,
            color: '#333',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            {displayLanguage === 'ar' ? 'تصفح المزيد من الرؤى' : 'Browse More Insights'}
          </h2>
          
          <p style={{
            textAlign: 'center',
            color: '#666',
            marginBottom: '40px',
            fontSize: '16px'
          }}>
            {displayLanguage === 'ar' ? 
              'اكتشف المزيد من التحليلات القانونية والتحديثات التنظيمية' : 
              'Discover more legal insights and regulatory updates'
            }
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '25px',
            marginBottom: '40px'
          }}>
            {relatedInsights.map((relatedInsight, index) => (
              <article
                key={relatedInsight.id}
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 6px 24px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onClick={(e) => handleRelatedInsightClick(relatedInsight, e)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)'
                  e.currentTarget.style.boxShadow = '0 16px 48px rgba(0, 0, 0, 0.12)'
                  e.currentTarget.style.borderColor = '#1a1a1a'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)'
                  e.currentTarget.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.08)'
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.05)'
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(0.98)'
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)'
                }}
              >
                <div style={{
                  height: '180px',
                  backgroundColor: '#000000',
                  backgroundImage: `url('${relatedInsight.image || relatedInsight.background_image || relatedInsight.featured_image || '/pic/' + ((index % 4) + 1) + '.jpg'}')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.7))'
                  }} />
                  
                  {/* Category Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '15px',
                    left: '15px'
                  }}>
                    <span style={{
                      padding: '6px 12px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      color: '#333',
                      borderRadius: '15px',
                      fontSize: '11px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {relatedInsight.Category || 'Insight'}
                    </span>
                  </div>
                  
                  {/* Navigation Arrow */}
                  <div style={{
                    position: 'absolute',
                    bottom: '15px',
                    right: '15px',
                    width: '40px',
                    height: '40px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </div>
                </div>
                
                <div style={{ padding: '24px' }}>
                  <h3 style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#333',
                    marginBottom: '12px',
                    lineHeight: 1.3,
                    textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                  }}>
                    {getTitle(relatedInsight)}
                  </h3>
                  
                  <p style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    color: '#666',
                    lineHeight: 1.6,
                    marginBottom: '16px',
                    textAlign: displayLanguage === 'ar' ? 'right' : 'left',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {getExcerpt(relatedInsight)}
                  </p>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '12px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#999'
                    }}>
                      <div>{formatDate(relatedInsight.Date)}</div>
                      {relatedInsight.Source && (
                        <div style={{ marginTop: '4px' }}>
                          {displayLanguage === 'ar' ? 'بقلم' : 'By'} {relatedInsight.Source}
                        </div>
                      )}
                    </div>
                    
                    <div style={{
                      color: '#1a1a1a',
                      fontWeight: '700',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      {displayLanguage === 'ar' ? 'اقرأ المزيد' : 'Read More'}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Navigation Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            marginTop: '50px',
            flexWrap: 'wrap'
          }}>
            <Link
              to="/#insights"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 28px',
                background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
                color: '#fff',
                textDecoration: 'none',
                borderRadius: '30px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 16px rgba(26, 26, 46, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)'
                e.target.style.boxShadow = '0 8px 32px rgba(26, 26, 46, 0.3)'
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)'
                e.target.style.boxShadow = '0 4px 16px rgba(26, 26, 46, 0.2)'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5"/>
                <path d="M12 19l-7-7 7-7"/>
              </svg>
              {displayLanguage === 'ar' ? 'عرض جميع الرؤى' : 'View All Insights'}
            </Link>
            
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 28px',
                background: 'transparent',
                border: '2px solid #1a1a1a',
                color: '#1a1a1a',
                borderRadius: '30px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#1a1a1a'
                e.target.style.color = '#fff'
                e.target.style.transform = 'translateY(-2px)'
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent'
                e.target.style.color = '#1a1a1a'
                e.target.style.transform = 'translateY(0)'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 15l-6-6-6 6"/>
              </svg>
              {displayLanguage === 'ar' ? 'العودة للأعلى' : 'Back to Top'}
            </button>
          </div>
        </div>
      </section>

      <div style={{ marginTop: 'auto', paddingTop: '60px' }}>
        <Footer />
      </div>
      
      {/* Responsive CSS for title-image section and sidebar */}
      <style>{`
        @media (max-width: 768px) {
          .title-image-section {
            flex-direction: column !important;
            gap: 20px !important;
            align-items: center !important;
          }
          .title-image-section > div:first-child {
            min-width: 100% !important;
            text-align: center !important;
          }
          .title-image-section > div:last-child {
            width: 100% !important;
            max-width: 500px !important;
            height: 350px !important;
          }
          
          /* Mobile: Stack content and services vertically */
          section > div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
          
          /* Mobile: Services come first in Arabic, content first in English */
          div[style*="order: 1"] {
            order: 1 !important;
          }
          div[style*="order: 2"] {
            order: 2 !important;
          }
          
          /* Mobile: Remove sticky positioning */
          div[style*="position: sticky"] {
            position: static !important;
          }
        }
        
        @media (max-width: 480px) {
          .title-image-section > div:last-child {
            width: 100% !important;
            max-width: 400px !important;
            height: 280px !important;
          }
        }
      `}</style>
    </div>
  )
}

export default DynamicInsightPage
